import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Skeleton } from '../../../components/ui/skeleton';
import { ArrowLeft, FileText, Building, Calendar, DollarSign, Package, User, Check, X, Edit, Trash2, Clock, MapPin, Phone, Mail, Printer, Download, Share2, AlertCircle, IndianRupee } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';

export default function QuoteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, delete: deleteApi } = useApi();
  const { can, PERMISSIONS } = usePermissions();
  const { user } = useAuth();
  
  const [quote, setQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false });

  useEffect(() => {
    if (id && user) {
      fetchQuoteDetails();
    }
  }, [id, user]);

  const fetchQuoteDetails = async () => {
    try {
      setIsLoading(true);
      
      console.group('📋 FETCHING QUOTE DETAILS');
      
      const response = await get(`/admin/quotes/${id}`);
      
      
      console.groupEnd();
      
      // Handle both wrapped and direct response formats
      const quoteData = response?.data || response;
      
      if (quoteData && (quoteData.id || quoteData._id)) {
        // Map the response data to match frontend expected names
        const mappedQuote = {
          ...quoteData,
          id: quoteData.id || quoteData._id,
          quotenumber: quoteData.quote_number || quoteData.quoteNumber,
          quote_number: quoteData.quote_number || quoteData.quoteNumber,
          total_amount: parseFloat(quoteData.total_amount || quoteData.amount || 0),
          created_at: quoteData.created_at || quoteData.createdAt,
          updated_at: quoteData.updated_at || quoteData.updatedAt,
        };
        
        setQuote(mappedQuote);
      } else {
        toast.error('Quote not found');
        navigate('/admin/quotes');
      }
      
    } catch (error) {
      console.group('❌ QUOTE DETAILS FETCH ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while fetching quote details.';
      
      if (error.response?.status === 404) {
        errorMessage = 'Quote not found.';
        navigate('/admin/quotes');
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteQuote = async () => {
    if (!quote) return;
    
    try {
      console.group('🗑️ DELETING QUOTE');
      
      const response = await deleteApi(`/admin/quotes/${quote.id}`);
      
      console.groupEnd();
      
      toast.success(response?.message || 'Quote deleted successfully!');
      navigate('/admin/quotes');
    } catch (error) {
      console.group('❌ QUOTE DELETE ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while deleting the quote.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleApproveQuote = async () => {
    if (!quote) return;
    
    try {
      console.group('✅ APPROVING QUOTE');
      
      const payload = {
        status: 'approved',
        items: (quote.items || []).map(item => ({
          id: item.id || item._id,
          unit_price: parseFloat(item.unit_price || item.unitPrice),
        }))
      };
      
      
      const response = await post(`/admin/quotes/${quote.id}/approve`, payload);
      
      console.groupEnd();
      
      // Update the quote status in local state
      setQuote(prev => ({ ...prev, status: 'approved' }));
      
      toast.success(response?.message || 'Quote approved successfully');
    } catch (error) {
      console.group('❌ QUOTE APPROVE ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while approving the quote.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleRejectQuote = async () => {
    if (!quote) return;
    
    try {
      console.group('❌ REJECTING QUOTE');
      
      const payload = {
        status: 'rejected',
        items: (quote.items || []).map(item => ({
          id: item.id || item._id,
          unit_price: parseFloat(item.unit_price || item.unitPrice),
        }))
      };
      
      
      const response = await post(`/admin/quotes/${quote.id}/reject`, payload);
      
      console.groupEnd();
      
      // Update the quote status in local state
      setQuote(prev => ({ ...prev, status: 'rejected' }));
      
      toast.success(response?.message || 'Quote rejected successfully');
    } catch (error) {
      console.group('❌ QUOTE REJECT ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while rejecting the quote.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100';
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      case 'active':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
      case 'inactive':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-100';
      case 'blocked':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100';
      case 'deleted':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      case 'cancelled':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded" />
            <div>
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Combined Quote and Company Details Card Skeleton */}
        <Card className="border-0 shadow-lg bg-muted/50">
          <CardHeader>
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Quote Details Skeleton */}
              <div className="lg:w-1/2 space-y-6">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-5 h-5 rounded" />
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-l border-gray-300 dark:border-gray-600 pl-8"></div>

              {/* Company Details Skeleton */}
              <div className="lg:w-1/2 space-y-6">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-5 h-5 rounded" />
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Show error state
  if (!quote) {
    return (
      <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
        <div className="flex flex-col items-center justify-center h-64">
          <FileText className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Quote Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested quote could not be found.</p>
          <Button onClick={() => navigate('/admin/quotes')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quotes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2 ">
          
          
        
        </div>
        
        {/* Quote Header Card */}
        <Card className="border-0 shadow-lg bg-muted/50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Quote Avatar */}
              <Avatar className="h-20 w-20 border-4 border-white dark:border-gray-800 shadow-lg">
                <AvatarImage src={`https://api.dicebear.com/7.x/shapes/svg?seed=${quote.quotenumber || quote.quote_number}&backgroundColor=3b82f6`} />
                <AvatarFallback className="bg-blue-500 text-white text-sm font-bold">
                  {quote.quotenumber?.substring(0, 2).toUpperCase() || 'QT'}
                </AvatarFallback>
              </Avatar>
              
              {/* Quote Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                  <h1 className="text-xl font-bold text-foreground">Quote #{quote.quotenumber || quote.quote_number}</h1>
                 
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Company:</span>
                    <span className="text-sm font-medium text-base">{quote.account?.name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Created:</span>
                    <span className="text-sm font-medium text-base">{formatDate(quote.created_at).split(',')[0]}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Total:</span>
                    <span className="text-sm font-medium text-base">{formatCurrency(quote.total_amount)}</span>
                  </div>
                </div>
              </div>
              
             
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Combined Quote and Company Details Card */}
      <Card className="border-0 shadow-lg bg-muted/50">
        <CardHeader>
          <CardTitle>
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Quote Details */}
              <div className="lg:w-1/2 space-y-6">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Quote Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Quote Number</p>
                    <p className="font-medium text-base text-base">{quote.quotenumber || quote.quote_number}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Created Date</p>
                    <p className="font-medium text-base">{formatDate(quote.created_at)}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium text-base">{quote.account?.name || 'N/A'}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Items Count</p>
                    <p className="font-medium text-base">{quote.items?.length || 0} items</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-medium text-base">{formatCurrency(quote.total_amount)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Account Status</p>
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 border-0 px-2 py-0.5">
                      {quote.account?.status.toUpperCase() || 'UNKNOWN'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="border-l border-gray-300 dark:border-gray-600 pl-8"></div>

              {/* Company Details */}
              <div className="lg:w-1/2 space-y-6">
                <div className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Company Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Company Name</p>
                    <p className="font-medium text-base">{quote.account?.name || 'N/A'}</p>
                  </div>
                  {quote.account?.email && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium text-base">{quote.account.email}</p>
                    </div>
                  )}
                  {quote.account?.phone && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium text-base">{quote.account.phone}</p>
                    </div>
                  )}
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-medium text-base">{formatCurrency(quote.total_amount)}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Items Count</p>
                    <p className="font-medium text-base">{quote.items?.length || 0} items</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className={`${getStatusBadgeColor(quote.status)} border-0 px-2 py-0.5`}>
                      {quote.status?.toUpperCase() || 'UNKNOWN'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Quote Items */}
      {quote.items && quote.items.length > 0 && (
        <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow mt-6">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400">
                  <Package className="w-5 h-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-lg">Quote Items</CardTitle>
                <p className="text-sm text-muted-foreground">Detailed breakdown of all items in this quote</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-sm font-semibold">Item</TableHead>
                    <TableHead className="text-sm font-semibold">Device Description</TableHead>
                    <TableHead className="text-sm font-semibold">Model Name</TableHead>
                    <TableHead className='text-sm font-semibold'>Firmware Version</TableHead>
                    <TableHead className='text-sm font-semibold'>Hardware Version</TableHead>
                    <TableHead className="text-right text-sm font-semibold">Quantity</TableHead>
                    <TableHead className="text-right text-sm font-semibold">Unit Price</TableHead>
                    <TableHead className="text-right text-sm font-semibold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.items.map((item, index) => (
                    <TableRow key={index} className="hover:bg-muted/50">
                      <TableCell className="font-medium text-base text-sm">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs">
                              {item.device_type?.name?.substring(0, 2).toUpperCase() || 'IT'}
                            </AvatarFallback>
                          </Avatar>
                          <span>{item.device_type?.name || item.name || item.itemName || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-foreground text-sm">
                        {item.device_type?.description || item.description || 'No description'}
                      </TableCell>
                      <TableCell className="text-foreground text-sm">
                        {item.device_type?.model_name || 'N/A'}
                      </TableCell>
                      <TableCell className="text-foreground text-sm">
                        {item.device_type?.firmware_version || 'N/A'}
                      </TableCell>
                      <TableCell className="text-foreground text-sm">
                        {item.device_type?.hardware_version || 'N/A'}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">
                        {item.quantity || 0}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">
                        {formatCurrency(parseFloat(item.unit_price || item.unitPrice || 0))}
                      </TableCell>
                      <TableCell className="text-right font-bold text-sm">
                        {formatCurrency(parseFloat(item.total_price || (item.quantity || 0) * (item.unit_price || item.unitPrice || 0)))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          
          {/* Fixed Footer with Total */}
          <div className="border-t border-border/50 bg-muted/30 px-3 py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Total Amount:</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-foreground">
                  {formatCurrency(quote.items.reduce((total, item) => {
                    const itemTotal = parseFloat(item.total_price || (item.quantity || 0) * (item.unit_price || item.unitPrice || 0));
                    return total + itemTotal;
                  }, 0))}
                </div>
                <div className="text-sm text-muted-foreground">
                  {quote.items.length} item{quote.items.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-border/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Quote
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete quote{' '}
                <span className="font-semibold text-foreground">
                  "{quote.quotenumber || quote.quote_number}"
                </span>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This will permanently delete the quote and all its data.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteDialog({ isOpen: false })}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteQuote}
                className="px-4 py-2 bg-red-600 hover:bg-red-700"
              >
                Delete Quote
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}